import React, { useEffect, useRef, useState } from "react";
import { Tour, type TourProps } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { TourRefsProvider } from "./TourContext";
import { useTourContext } from "./useTourContext";
import { useResponsive } from "../../hooks/useResponsive";

const hiddenButtons = {
  nextButtonProps: { style: { display: "none" } as React.CSSProperties },
  prevButtonProps: { style: { display: "none" } as React.CSSProperties },
};

const STEP_DELAY_MS = 800;

const S: React.CSSProperties = { fontSize: 14, lineHeight: 1.5, padding: 12 };

const T = (text: React.ReactNode) => <div style={S}>{text}</div>;

const TourInner: React.FC = () => {
  const {
    isTourOpen,
    tourStep,
    setTourStep,
    finishTour,
    ordersNavRef,
    mobileOrdersMenuRef,
    mobileMenuBtnRef,
    menuNavRef,
    addCategoryBtnRef,
    categoryNameInputRef,
    addProductBtnRef,
    productNameInputRef,
    productPriceInputRef,
    productDescInputRef,
    globeRef,
    stepIndexes,
  } = useTourContext();

  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const [visibleStep, setVisibleStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const justOpenedRef = useRef(false);

  useEffect(() => {
    if (isTourOpen) {
      justOpenedRef.current = true;
      setVisibleStep(tourStep);
      setIsVisible(true);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      setIsVisible(false);
    }
  }, [isTourOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isTourOpen || !isMobile) return;

    const handleUpdate = () => {
      window.dispatchEvent(new Event("resize"));
    };

    document.addEventListener("scroll", handleUpdate, true);
    window.visualViewport?.addEventListener("resize", handleUpdate);
    window.visualViewport?.addEventListener("scroll", handleUpdate);

    const handleFocus = () => {
      let count = 0;
      const t = setInterval(() => {
        handleUpdate();
        count++;
        if (count > 5) clearInterval(t);
      }, 100);
    };
    document.addEventListener("focusin", handleFocus);

    return () => {
      document.removeEventListener("scroll", handleUpdate, true);
      window.visualViewport?.removeEventListener("resize", handleUpdate);
      window.visualViewport?.removeEventListener("scroll", handleUpdate);
      document.removeEventListener("focusin", handleFocus);
    };
  }, [isTourOpen, isMobile]);

  useEffect(() => {
    if (!isTourOpen) return;
    if (justOpenedRef.current) {
      justOpenedRef.current = false;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsVisible(false);
    timerRef.current = setTimeout(() => {
      setVisibleStep(tourStep);
      setIsVisible(true);
    }, STEP_DELAY_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [tourStep, isTourOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => finishTour();
  const handleChange = (current: number) => setTourStep(current);

  const steps: TourProps["steps"] = [];

  // [0] WELCOME
  steps.push({
    title: null,
    description: T(
      <Trans
        i18nKey="tourContextProvider.welcomeDescription"
        components={{ strong: <strong />, br: <br /> }}
      />
    ),
    target: null,
    nextButtonProps: { children: t("tourContextProvider.startButton") },
    prevButtonProps: { style: { display: "none" } },
  });

  if (isMobile) {
    // [1] HAMBURGER_1
    steps.push({
      title: null,
      description: T(
        <Trans
          i18nKey="tourContextProvider.hamburger1Description"
          components={{ strong: <strong /> }}
        />
      ),
      target: () => mobileMenuBtnRef.current as HTMLElement,
      placement: "bottom",
      ...hiddenButtons,
    });
  }

  // [Desktop 1 / Mobile 2] ORDERS_NAV
  steps.push({
    title: null,
    description: T(<>
      🔔{" "}
      {isMobile
        ? t("tourContextProvider.ordersNavBellMobile")
        : t("tourContextProvider.ordersNavBellDesktop")}{" "}
      <Trans
        i18nKey="tourContextProvider.ordersNavDescription"
        components={{ strong: <strong /> }}
      />
    </>),
    target: () => {
      if (isMobile) {
        const el = mobileOrdersMenuRef.current?.querySelector('li[data-menu-id="orders"]');
        return (el ?? mobileOrdersMenuRef.current) as HTMLElement;
      }
      return ordersNavRef.current as HTMLElement;
    },
    placement: isMobile ? "bottom" : "right",
    ...hiddenButtons,
  });

  // [Desktop 2 / Mobile 3] MENU_NAV
  steps.push({
    title: null,
    description: T(<>
      <Trans
        i18nKey="tourContextProvider.menuNavPrefix"
        components={{ strong: <strong /> }}
      />
      {isMobile
        ? t("tourContextProvider.menuNavLocationMobile")
        : t("tourContextProvider.menuNavLocationDesktop")}?{" "}
      <Trans
        i18nKey="tourContextProvider.menuNavSuffix"
        components={{ strong: <strong /> }}
      />
    </>),
    target: () => menuNavRef.current as HTMLElement,
    placement: "bottom",
    ...hiddenButtons,
  });

  // [Desktop 3 / Mobile 4] ADD_CATEGORY_BTN
  steps.push({
    title: null,
    description: T(
      <Trans
        i18nKey="tourContextProvider.addCategoryDescription"
        components={{ strong: <strong /> }}
      />
    ),
    target: () => addCategoryBtnRef.current as HTMLElement,
    placement: isMobile ? "bottom" : "bottomLeft",
    ...hiddenButtons,
  });

  // [Desktop 4 / Mobile 5] CATEGORY_MODAL_OPEN (hidden)
  steps.push({
    title: null,
    description: <></>,
    target: null,
    className: "hidden-tour-step",
    mask: false,
  });

  // [Desktop 5 / Mobile 6] ADD_PRODUCT_BTN
  steps.push({
    title: null,
    description: T(
      <Trans
        i18nKey="tourContextProvider.addProductDescription"
        components={{ strong: <strong /> }}
      />
    ),
    target: () => addProductBtnRef.current as HTMLElement,
    placement: isMobile ? "bottom" : "bottomLeft",
    ...hiddenButtons,
  });

  // [Desktop 6 / Mobile 7] PRODUCT_MODAL_OPEN (hidden)
  steps.push({
    title: null,
    description: <></>,
    target: null,
    className: "hidden-tour-step",
    mask: false,
  });

  if (isMobile) {
    // [Mobile 8] HAMBURGER_2
    steps.push({
      title: null,
      description: T(
        <Trans
          i18nKey="tourContextProvider.hamburger2Description"
          components={{ strong: <strong /> }}
        />
      ),
      target: () => mobileMenuBtnRef.current as HTMLElement,
      placement: "bottom",
      ...hiddenButtons,
    });

    // [Mobile 9] GLOBE (inside drawer)
    steps.push({
      title: null,
      description: T(
        <Trans
          i18nKey="tourContextProvider.globeMobileDescription"
          components={{ strong: <strong /> }}
        />
      ),
      target: () => globeRef.current as HTMLElement,
      placement: "bottom",
      ...hiddenButtons,
    });
  } else {
    // [Desktop 7] GLOBE
    steps.push({
      title: null,
      description: T(
        <Trans
          i18nKey="tourContextProvider.globeDesktopDescription"
          components={{ strong: <strong /> }}
        />
      ),
      target: () => globeRef.current as HTMLElement,
      placement: "right",
      ...hiddenButtons,
    });
  }

  // END
  steps.push({
    title: null,
    description: T(
      <Trans
        i18nKey="tourContextProvider.endDescription"
        components={{ strong: <strong /> }}
      />
    ),
    target: null,
    nextButtonProps: { children: t("tourContextProvider.finishButton"), onClick: finishTour },
    prevButtonProps: { style: { display: "none" } },
  });

  const isHiddenStep =
    tourStep === stepIndexes.PRODUCT_MODAL_OPEN ||
    tourStep === stepIndexes.CATEGORY_MODAL_OPEN;

  return (
    <Tour
      open={isVisible && !isHiddenStep}
      onClose={handleClose}
      steps={steps}
      current={visibleStep}
      onChange={handleChange}
      scrollIntoViewOptions={isMobile ? false : { behavior: "smooth", block: "center" }}
      mask={{ style: { boxShadow: "inset 0 0 15px #333" } }}
      type="default"
      zIndex={99999}
    />
  );
};

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <TourRefsProvider>
      <style>{`
        .hidden-tour-step {
          display: none !important;
        }
        .ant-tour {
          max-width: min(320px, calc(100vw - 24px)) !important;
        }
        .ant-tour-inner {
          padding: 12px !important;
        }
        .ant-tour-header {
          padding: 0 !important;
          min-height: 0 !important;
        }
        .ant-tour-content {
          padding: 0 !important;
        }
        .ant-tour-description {
          padding: 0 !important;
          margin: 0 !important;
        }
        .ant-tour-footer {
          padding: 0 12px 12px 12px !important;
          margin-top: 8px !important;
          display: flex !important;
          align-items: center !important;
        }
        .ant-tour-indicators {
          align-items: center !important;
        }
        .ant-tour-close {
          top: 10px !important;
          right: 10px !important;
        }
      `}</style>
      <TourInner />
      {children}
    </TourRefsProvider>
  );
};
